import React, { useState, useEffect } from "react";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { CLINIC, whatsappLink } from "../lib/clinic";
import { LogoMark } from "./LogoMark";

const links = [
  { label: "Symptoms", href: "#symptoms" },
  { label: "Quiz", href: "#quiz" },
  { label: "How it works", href: "#how" },
  { label: "Treatments", href: "#treatments" },
  { label: "NESA", href: "#nesa" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQs", href: "#faq" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0F1B2C]/85 border-b border-rb-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-2.5 shrink-0 text-rb-text"
        >
          <LogoMark size={28} className="text-[#93C5FD]" />
          <span className="font-serif-display text-2xl text-rb-text leading-none">
            Clenchless
          </span>
          <span className="hidden sm:inline font-overline mt-0.5">
            {CLINIC.brandTagline}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="text-sm text-rb-text-2 hover:text-rb-text transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <a
            href={CLINIC.phoneRaw}
            data-testid="header-call-btn"
            className="text-sm text-rb-text-2 hover:text-rb-text inline-flex items-center gap-1.5 px-3 py-2"
          >
            <Phone size={15} strokeWidth={1.5} />
            {CLINIC.phoneDisplay}
          </a>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp-btn"
            className="btn-primary text-sm inline-flex items-center gap-2"
          >
            <MessageCircle size={15} strokeWidth={1.75} />
            Enquire on WhatsApp
          </a>
        </div>

        <button
          className="md:hidden p-2 text-rb-text"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#0F1B2C] border-t border-rb-border"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                className="text-base text-rb-text-2 hover:text-rb-text"
              >
                {l.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 mt-2">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-whatsapp-btn"
                className="btn-primary text-sm text-center inline-flex items-center justify-center gap-2"
              >
                <MessageCircle size={15} /> Enquire on WhatsApp
              </a>
              <a
                href={CLINIC.phoneRaw}
                data-testid="mobile-call-btn"
                className="btn-secondary text-sm text-center inline-flex items-center justify-center gap-2"
              >
                <Phone size={15} /> {CLINIC.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
